import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGlobalSearch } from "@/lib/api";
import { getPostHogClient } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { CarrierSyncBanner } from "@/components/layout/CarrierSyncBanner";
import {
  Building2,
  FileText,
  History,
  IdCard,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Monitor,
  Phone,
  Search,
  Settings,
  Target,
  Users,
  X
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
  { icon: Users, label: "Insureds", href: "/insureds" },
  { icon: FileText, label: "Policies", href: "/policies" },
  { icon: Building2, label: "Companies", href: "/companies" },
];

function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 500);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isDebouncing = query.trim() !== debounced;
  const { data, isLoading: isFetching } = useGlobalSearch(debounced);
  const isSearching = isDebouncing || isFetching;
  const insureds = data?.insureds || [];
  const policyResults = data?.policies || [];
  const total = insureds.length + policyResults.length;

  function go(path: string) {
    navigate(path);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={containerRef} className="relative max-w-lg w-full">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search by DOT, business name, e-mail, phone number, or policy #"
        className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20 transition-all placeholder:text-muted-foreground/60"
      />
      {query && (
        <button
          type="button"
          onClick={() => { setQuery(""); setOpen(false); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {open && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[480px] overflow-y-auto">
          {isSearching && <div className="px-4 py-3 text-sm text-muted-foreground">Searching...</div>}
          {!isSearching && total === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">No matches found.</div>
          )}
          {insureds.map((result, idx) => (
            <button
              key={`i-${result.id || idx}`}
              onClick={() => result.id ? go(`/insureds/${result.id}`) : undefined}
              className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 flex gap-3 items-start cursor-pointer"
            >
              <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <IdCard className="w-4 h-4 text-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{result.name}</div>
                {result.dotNumber && (
                  <div className="text-xs text-muted-foreground mt-0.5">DOT {result.dotNumber}</div>
                )}
                {result.telephone && (
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <Phone className="w-3 h-3" /> {result.telephone}
                  </div>
                )}
                {result.email && (
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> {result.email}
                  </div>
                )}
              </div>
            </button>
          ))}
          {policyResults.map((policy) => (
            <button
              key={`p-${policy.id}`}
              onClick={() => go(`/policies/${policy.id}`)}
              className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 flex gap-3 items-start cursor-pointer"
            >
              <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <FileText className="w-4 h-4 text-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{policy.policyNumber}</div>
                {(policy.insuredName ?? policy.customerName) && (
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">{policy.insuredName ?? policy.customerName}</div>
                )}
                {policy.carrier && (
                  <div className="text-xs text-muted-foreground mt-0.5">{policy.carrier}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ onMobileMenuToggle }: { onMobileMenuToggle?: () => void }) {
  const { user } = useAuth();
  const displayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.email || "User";
  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (user?.email?.[0] || "U").toUpperCase();

  return (
    <header className="relative h-14 bg-card border-b border-border flex items-center justify-between px-4 sm:px-6 z-20 shrink-0">
      <div className="flex items-center gap-3 flex-1">
        {onMobileMenuToggle && (
          <Button variant="ghost" size="icon" onClick={onMobileMenuToggle} className="md:hidden" data-testid="button-mobile-menu">
            <Menu className="w-5 h-5" />
          </Button>
        )}
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full ring-2 ring-border/50" data-testid="button-user-menu">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.profileImageUrl || ""} alt={displayName} />
                <AvatarFallback className="bg-sidebar-primary text-white">{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{displayName}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email || ""}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <Link href="/settings">
              <DropdownMenuItem className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
            </Link>
            <Link href="/system-settings">
              <DropdownMenuItem className="cursor-pointer">
                <Monitor className="mr-2 h-4 w-4" />
                System Settings
              </DropdownMenuItem>
            </Link>
            <Link href="/activity-log">
              <DropdownMenuItem className="cursor-pointer">
                <History className="mr-2 h-4 w-4" />
                Activity Log
              </DropdownMenuItem>
            </Link>
            {user?.crmAccess && (
              <Link href="/leads">
                <DropdownMenuItem className="cursor-pointer">
                  <Target className="mr-2 h-4 w-4" />
                  Leads
                </DropdownMenuItem>
              </Link>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="text-destructive focus:text-destructive cursor-pointer">
              <a
                href="/api/logout"
                data-testid="button-logout"
                onClick={() => {
                  // Best-effort: clear PostHog identity before the browser navigates
                  // to /api/logout. The redirect can race, but this mirrors the
                  // trade-off accepted on DC and is the simplest robust approach
                  // given AMS's plain-anchor logout.
                  getPostHogClient()?.reset();
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function Layout({
  children,
  fullWidth = false,
}: {
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  const [location] = useLocation();
  const isMobile = useIsMobile();
  const [mobileMenu, setMobileMenu] = useState(false);
  const { user } = useAuth();

  const displayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.email || "User";
  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (user?.email?.[0] || "U").toUpperCase();

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row w-full overflow-hidden">
      <aside className="hidden md:flex flex-col w-[60px] bg-card border-r border-border shrink-0 z-20">
        <div className="h-14 flex items-center justify-center border-b border-border">
          <div className="rounded p-1 flex items-center justify-center">
            <img
              src="/favicon.png"
              alt="LogRock"
              className="h-6 w-6 object-contain"
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center py-4 gap-1">
          {NAV_ITEMS.map((item) => {
            const isActive = item.href === "/dashboard"
              ? location === "/dashboard"
              : item.href === "/policies"
                ? location === "/policies" || location.startsWith("/policies/")
                : location.startsWith(item.href);
            return (
              <Link key={item.href} href={item.href} className="block">
                <div
                  className={cn(
                    "relative w-10 h-10 flex items-center justify-center rounded-2xl transition-all duration-150 cursor-pointer group",
                    isActive
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  title={item.label}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                  <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                    {item.label}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-2 pb-4">
          <Avatar className="h-10 w-10 rounded-2xl">
            <AvatarImage src={user?.profileImageUrl || ""} alt={displayName} />
            <AvatarFallback className="bg-foreground text-background text-xs font-bold rounded-2xl">{initials}</AvatarFallback>
          </Avatar>
        </div>
      </aside>

      {mobileMenu && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 cursor-pointer bg-black/30" onClick={() => setMobileMenu(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[280px] bg-card border-r border-border p-4 animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="bg-white rounded p-1 flex items-center justify-center">
                  <img src="/favicon.png" alt="LogRock" className="h-6 w-6 object-contain" />
                </div>
                <span className="font-heading font-semibold text-sm">AMS</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenu(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-1">
              {NAV_ITEMS.map((item) => {
                const isActive = item.href === "/dashboard"
                  ? location === "/dashboard"
                  : item.href === "/policies"
                    ? location === "/policies" || location.startsWith("/policies/")
                    : location.startsWith(item.href);
                return (
                  <Link key={item.href} href={item.href} className="block" onClick={() => setMobileMenu(false)}>
                    <div className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      isActive
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:bg-muted"
                    )}>
                      <item.icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <Header onMobileMenuToggle={isMobile ? () => setMobileMenu(true) : undefined} />
        <CarrierSyncBanner />
        <main className="flex-1 overflow-auto bg-background">
          {fullWidth ? (
            <div className="w-full animate-in fade-in duration-500">{children}</div>
          ) : (
            <div className="p-4 md:p-6">
              <div className="max-w-7xl mx-auto w-full animate-in fade-in duration-500">
                {children}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
